import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import googleDrive from '../../../assets/Images/googledrive.png';
import dropbox from '../../../assets/Images/dropbox.png';
import box from '../../../assets/Images/box.png';
import googleCalendar from '../../../assets/Images/googlecalendar.png';
import onedrive from '../../../assets/Images/onedrive.png';
import docusafe from '../../../assets/Images/smallogo.png';

const Integrations = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  const navigate = useNavigate();

  const integrations = [
    {
      id: 1,
      name: 'Google Drive',
      description: 'Connect and sync your Google Drive files seamlessly with our platform.',
      status: 'Available',
      users: '85K+',
      category: 'Storage',
      route: '/integrations/google-drive',
      image: googleDrive
    },
    {
      id: 2,
      name: 'Dropbox',
      description: 'Integrate Dropbox for easy file sharing and collaboration.',
      status: 'Available',
      users: '72K+',
      category: 'Storage',
      route: '/integrations/dropbox',
      image: dropbox
    },
    {
      id: 3,
      name: 'Box',
      description: 'Connect Box for secure enterprise file management and sharing.',
      status: 'Available',
      users: '45K+',
      category: 'Storage',
      route: '/integrations/box',
      image: box
    },
    {
      id: 4,
      name: 'OneDrive',
      description: 'Integrate Microsoft OneDrive for cloud storage and file synchronization.',
      status: 'Available',
      users: '58K+',
      category: 'Storage',
      route: '/integrations/onedrive',
      image: onedrive
    },
    {
      id: 5,
      name: 'Google Calendar',
      description: 'Sync your Google Calendar events and set up smart reminders for meetings and deadlines.',
      status: 'Available',
      users: '68K+',
      category: 'Productivity',
      route: '/integrations/google-calendar',
      image: googleCalendar
    },
  ];

  const categories = ['All', 'Storage', 'Productivity'];

  const filteredIntegrations = activeFilter === 'All'
    ? integrations
    : integrations.filter(integration => integration.category === activeFilter);

  const handleViewMore = (integration) => {
    setSelectedIntegration(integration);
  };

  // Helper function to render text with bold tags as JSX
  const renderTextWithBold = (text) => {
    if (!text) return '';

    // Split the text by <strong> tags and render accordingly
    const parts = text.split(/(<strong>.*?<\/strong>)/);
    return parts.map((part, index) => {
      if (part.startsWith('<strong>') && part.endsWith('</strong>')) {
        const boldText = part.replace(/<\/?strong>/g, '');
        return <strong key={index}>{boldText}</strong>;
      }
      return part;
    });
  };

  const getIntegrationContent = (integrationName) => {
    const content = {
      'Google Drive': {
        header: "Google Drive lets you store and access photos, documents, and more from anywhere.",
        description: '<strong>Google Drive</strong> is a cloud-based file storage service by Google that allows users to store and access photos, documents and more from anywhere. With the <strong>DocuSafe</strong> integration, you can seamlessly connect your document workflows with Google Drive.',
        features: [
          'Collect files from users through sleeve in <strong>DocuSafe</strong>.',
          'Automatically sync uploaded documents, images, and other files to your Google Drive.',
          'Easily organize, access, and share files with your team or clients.'
        ],
        summary: 'This integration streamlines file management and ensures your important documents are securely stored and readily accessible in the cloud.'
      },
      'Dropbox': {
        header: "Dropbox lets you store and access photos, documents, and more from anywhere.",
        description: '<strong>Dropbox</strong> is a popular cloud storage platform that allows you to store, access, and share files such as documents, images and more from anywhere. With the <strong>DocuSafe</strong> integration, you can seamlessly streamline your file collection and storage process using Dropbox.',
        features: [
          'Collect files from users through sleeves in <strong>DocuSafe</strong>.',
          'Automatically sync uploaded documents, images, and other files to your Dropbox account.',
          'Effortlessly organize, access, and share files with your team or clients.'
        ],
        summary: 'This integration ensures your important files are securely stored and easily retrievable within your Dropbox workspace.'
      },
      'Box': {
        header: "Box Drive lets you store and access photos, documents and more from anywhere.",
        description: '<strong>Box Drive</strong> is a secure cloud content management solution designed for businesses to collaborate, store, and manage files efficiently. With the <strong>DocuSafe</strong> integration, you can bring your document collection and storage workflow into the Box ecosystem.',
        features: [
          'Collect files from users through sleeves in <strong>DocuSafe</strong>.',
          'Automatically sync uploaded documents, images, and other files to your Box Drive account.',
          'Maintain organized, secure, and compliant document storage while enabling seamless collaboration.'
        ],
        summary: 'This integration helps centralize your document handling process while leveraging Box\'s enterprise-grade security and sharing capabilities.'
      },
      'OneDrive': {
        header: "OneDrive lets you store and access photos, documents, videos, and more from anywhere.",
        description: '<strong>OneDrive</strong> is <strong>Microsoft\'s</strong> cloud storage solution that allows you to store, access, and share files like documents, images, and videos from anywhere. With the <strong>DocuSafe</strong> integration, you can seamlessly connect your document workflows with OneDrive.',
        features: [
          'Collect files from users through sleeves in <strong>DocuSafe</strong>.',
          'Automatically sync uploaded documents, images, and other files to your OneDrive account.',
          'Easily organize, access, and collaborate on files with your team or clients.'
        ],
        summary: 'This integration enhances your document collection process while keeping everything securely backed up and accessible via the Microsoft ecosystem.'
      },
      'Google Calendar': {
        header: "Google Calendar lets you schedule events, set reminders, manage tasks, share calendars, and stay organized across all your synced devices.",
        description: '<strong>Google Calendar</strong> is a powerful tool that helps you schedule events, send invitations, and set reminders with ease. It syncs seamlessly across all your devices, allowing you to stay updated wherever you are. You can share calendars with others, manage daily tasks, check others’ availability, and keep your day well-organized, making time management simple and effective.',
        features: [
          'In <strong>DocuSafe</strong>, set a reminder for the sleeve due date to collect documents from your customer.',
          'Set a reminder for a specific document ',
          'Sync your Google Calendar events and set up smart reminders for meetings and deadlines.'
        ],
        summary: ''
      },

    };

    return content[integrationName] || content['Google Drive'];
  };



  const renderIntegrationDetails = () => (
    <div className="integration-details-page">


      {/* Header Section */}
      <div className="integration-header">
        <div className="integration-header-container">
          {/* Breadcrumb */}
          <div className="breadcrumb-container">
            <nav className="breadcrumb-nav">
              <span className="breadcrumb-item">INTEGRATIONS</span>
              <span>/</span>
              <span className="breadcrumb-current">
                {selectedIntegration.name}
              </span>
            </nav>
          </div>

          {/* Main Header Content */}
          <div className="header-content-grid">
            {/* Left Content */}
            <div className="header-left-content">
              <h1 className="integration-main-title">
                {selectedIntegration.name} Integration
              </h1>

              <p className="integration-description">
                {getIntegrationContent(selectedIntegration.name).header || ''}
              </p>

              {/* Back Button */}
              <div className="back-button-container">
                <button
                  className="back-button"
                  onClick={() => setSelectedIntegration(null)}
                >
                  <i className="bi bi-arrow-left"></i>
                  Back to Integrations
                </button>
              </div>
            </div>



            {/* Right Visual */}
            <div className="integration-visual-container">
              {/* Background decoration */}
              <div className="visual-bg-decoration"></div>

              {/* DocuSafe Logo */}
              <div className="logo-container">
                <img
                  src={docusafe}
                  alt="DocuSafe"
                  className="logo-image"
                />
              </div>

              {/* Plus Sign */}
              <div className="plus-connector">
                +
              </div>

              {/* Integration Logo */}
              <div className="logo-container">
                <img
                  src={selectedIntegration.image}
                  alt={selectedIntegration.name}
                  className="logo-image"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="integration-main-content">
        {/* Overview Card */}
        <div className="overview-card">
          <div className="overview-content">
            {/* Description */}
            <div className="description-section">
              <p className="description-text">
                {renderTextWithBold(getIntegrationContent(selectedIntegration.name).description)}
              </p>
            </div>

            {/* Features Section */}
            <div className="integration-features">
              <h2 className="features-title">
                Using this integration, you can:
              </h2>

              <div className="features-grid">
                {getIntegrationContent(selectedIntegration.name).features.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <div className="intigration-icon">
                      <i className="bi bi-check"></i>
                    </div>
                    <p className="feature-text">
                      {renderTextWithBold(feature)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom Summary */}
            <div className="summary-section">
              <div className="summary-icon">
                <i className="bi bi-shield-check"></i>
              </div>
              <h3 className="summary-title">
                Streamlined Document Management
              </h3>
              <p className="summary-text">
                {getIntegrationContent(selectedIntegration.name).summary}
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );

  return (
    <div className="integrations-page">
      {selectedIntegration ? (
        renderIntegrationDetails()
      ) : (
        <>
          {/* Header Section */}
          <div className="integrations-header">
            <div className="integrations-header-content">
              <div className="integrations-icon">

              </div>
              <h1 className="integrations-title">Powerful Integrations</h1>
              {/* <p className="integrations-subtitle">
                Connect all your favorite tools and automate your workflow with our extensive integration library.
              </p> */}
              {/* <div className="integrations-stats">
                <div className="stat-item">
                  <i className="bi bi-star-fill"></i>
                  <span>5 Integrations</span>
                </div>
              </div> */}
            </div>
          </div>

          {/* Content Section */}
          <div className="integrations-content">
            {/* Filter Buttons */}
            <div className="integrations-filters">
              {categories.map(category => (
                <button
                  key={category}
                  className={`filter-btn ${activeFilter === category ? 'active' : ''}`}
                  onClick={() => setActiveFilter(category)}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Integration Cards */}
            <div className="integrations-grid">
              {filteredIntegrations.map(integration => (
                <div key={integration.id} className="integration-card">
                  <div className="integration-card-header">
                    <div className="integration-icon-wrapper">
                      <img
                        src={integration.image}
                        alt={integration.name}
                        className="integration-icon-image"
                      />
                    </div>
                    <div className="integration-info">
                      <h3 className="integration-name">{integration.name}</h3>
                      <span className={`integration-status ${integration.status.toLowerCase()}`}>
                        {integration.status}
                      </span>
                    </div>
                  </div>

                  <p className="integration-description">
                    {integration.description}
                  </p>

                  <div className="integration-footer">
                    <button
                      className="connect-btn"
                      onClick={() => handleViewMore(integration)}
                    >
                      View More
                      <i className="bi bi-arrow-right"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Integrations;